import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../api';
import { connectEndpoints } from '../api-connect';
import { NotPermitted } from '../App';
import { StripeAccountCard } from '../components/StripeAccountCard';
import { PlatformFeeSchedulePanel } from '../components/PlatformFeeSchedule';
import { usePermissions } from '../session';
import { Badge, Button, ErrorNote, Panel, Table, formatWhen } from '../ui';
import { INPUT } from './event/shared';

/**
 * The STGD section (D-393): what STGD, the platform, runs for this team and
 * the team's staff never see. Its permissions are platform-reserved: no role
 * holds them and no admin can grant them; they come only from signing in as
 * an address in the Worker setting PLATFORM_STAFF_EMAILS. The menu hides the
 * section from everybody else, and the API refuses them regardless.
 *
 * It holds the team's Stripe account seen from STGD's side (the full id, its
 * history, and disconnecting it), and STGD's per-ticket fee schedule (D-394).
 */
export default function PlatformPage() {
  const can = usePermissions();
  const view = useQuery({ queryKey: ['platform', 'stripe-account'], queryFn: connectEndpoints.platformAccount, enabled: can('platform.access') });
  if (!can('platform.access')) return <NotPermitted permission="platform.access" />;
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-ink-900">STGD platform</h1>
        <p className="mt-1 text-sm text-ink-600">STGD’s side of this team’s deployment. Only STGD staff see this section; the team’s own staff, admins included, cannot open it or be given access to it.</p>
      </div>
      {view.error ? <ErrorNote>{(view.error as ApiError).message}</ErrorNote> : null}
      <PlatformFeeSchedulePanel />
      {can('platform.stripe_account.manage') ? <StripeAccountCard door="platform" view={view.data} queryKey={['platform', 'stripe-account']} /> : null}
      {can('platform.stripe_account.manage') && view.data?.account ? <DisconnectPanel /> : null}
      <Panel title="Stripe account history" description="Every account this deployment has been connected to, newest first.">
        <Table headers={['Account', 'Mode', 'How', 'Status', 'Connected', 'Disconnected']}>
          {(view.data?.history ?? []).map((row) => (
            <tr key={row.id}>
              <td className="px-4 py-2 font-mono text-xs">{row.stripe_account_id}</td>
              <td className="px-4 py-2 text-sm">{row.livemode ? 'Live' : 'Test'}</td>
              <td className="px-4 py-2 text-sm">{row.connection_method === 'OAUTH' ? 'Existing account' : 'New account'}</td>
              <td className="px-4 py-2"><Badge tone={row.status === 'ENABLED' ? 'ok' : row.status === 'DISCONNECTED' || row.status === 'DISABLED' ? 'bad' : 'warn'}>{row.status}</Badge></td>
              <td className="px-4 py-2 text-sm">{formatWhen(row.connected_at)}</td>
              <td className="px-4 py-2 text-sm">{row.disconnected_at ? `${formatWhen(row.disconnected_at)}: ${row.disconnected_reason ?? ''}` : '—'}</td>
            </tr>
          ))}
        </Table>
      </Panel>
    </div>
  );
}

/** Disconnecting is STGD's lever alone; the team disconnects from its own Stripe Dashboard. */
function DisconnectPanel() {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const disconnect = useMutation({
    mutationFn: () => connectEndpoints.disconnect(reason.trim()),
    onSuccess: (result) => {
      setError(null);
      setReason('');
      setNote(result.deauthorized ? 'Disconnected here and at Stripe.' : `Disconnected here. ${result.message ?? ''}`);
      void queryClient.invalidateQueries({ queryKey: ['platform', 'stripe-account'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  return (
    <Panel title="Disconnect the Stripe account" description="Online card sales stop at once. Card refunds of charges already made then have to be done by the team in its own Stripe Dashboard.">
      <div className="space-y-3 px-5 py-4">
        {error ? <ErrorNote>{error}</ErrorNote> : null}
        {note ? <p className="text-sm text-ink-600">{note}</p> : null}
        <label htmlFor="disconnect-reason" className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Why</label>
        <input id="disconnect-reason" className={`${INPUT} w-full`} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="For the audit log" />
        <Button
          variant="danger"
          disabled={reason.trim().length < 3 || disconnect.isPending}
          onClick={() => {
            if (window.confirm('Disconnect the team’s Stripe account? Online card sales stop at once.')) disconnect.mutate();
          }}
        >
          {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
        </Button>
      </div>
    </Panel>
  );
}
